import { AlertTriangle, Mail, Phone, ShieldCheck, ExternalLink } from "lucide-react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";

export default function Signalement() {
  return (
    <>
      <Seo
        title="Signalement — violences, harcèlement, discrimination"
        description="Procédure de signalement des situations de violence, harcèlement moral ou sexuel, agissements sexistes et discriminations dans les centres du regroupement EDUCASER GROUP."
        path="/signalement"
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Signalement" }]}
        eyebrow="Décret novembre 2026"
        title="Signaler une situation"
        intro="Une situation de violence, de harcèlement, d'agissement sexiste ou de discrimination ? Nos centres mettent à votre disposition un canal dédié, confidentiel et sans jugement."
      />
      <div className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-3xl space-y-10 px-5 text-slate-600 lg:px-8">
          <section className="rounded-3xl border border-brand-red/20 bg-brand-red/5 p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-brand-red" />
              <p className="text-sm">
                En cas de danger immédiat, appelez le <strong>17 (Police)</strong> ou le{" "}
                <strong>112 (numéro d'urgence européen)</strong>. Pour un signalement en ligne :{" "}
                <a href="https://www.service-public.fr/particuliers/vosdroits/N31138" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand-red underline">service-public.fr</a>.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-blue">Qui peut signaler ?</h2>
            <p className="mt-3 text-sm">
              Tout apprenant, salarié, formateur, prestataire ou visiteur d'un centre du regroupement
              EDUCASER GROUP, ainsi que toute personne témoin d'une situation dans le cadre d'une
              formation ou d'une prestation.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-blue">Ce qui peut être signalé</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>• Violences physiques ou verbales</li>
              <li>• Harcèlement moral ou sexuel</li>
              <li>• Agissements sexistes</li>
              <li>• Discriminations (origine, sexe, âge, handicap, orientation, religion, opinion, etc.)</li>
              <li>• Comportements inappropriés ou intimidants</li>
            </ul>
          </section>

          <section className="rounded-2xl border border-slate-200 p-6">
            <h2 className="font-display text-xl font-bold text-brand-blue">Canal de signalement</h2>
            <p className="mt-3 text-sm">
              Vous pouvez adresser votre signalement à notre référent, de manière strictement
              confidentielle.
            </p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-red" />
                <strong>Référente signalement :</strong> Manon Michelis
              </p>
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand-red" />
                <a href="mailto:manon.michelis@educaser.fr" className="hover:text-brand-blue">manon.michelis@educaser.fr</a>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-brand-red" />
                <a href="tel:0698558031" className="hover:text-brand-blue">06 98 55 80 31</a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-blue">Comment est traité votre signalement ?</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>• Confidentialité absolue de l'identité du signalant et des personnes concernées.</li>
              <li>• Accusé de réception sous <strong>7 jours ouvrés</strong>.</li>
              <li>• Entretien confidentiel avec le référent proposé sous 15 jours.</li>
              <li>• Analyse des faits et mesures adaptées (protection, médiation, sanction, saisine externe si nécessaire).</li>
              <li>• Retour du référent sur le traitement engagé.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold text-brand-blue">Saisines externes possibles</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>• <a href="https://www.defenseurdesdroits.fr/" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline">Défenseur des droits <ExternalLink className="inline h-3 w-3" /></a> — discriminations, relations avec les services publics.</li>
              <li>• <a href="https://arrepss.solidarites-sante.gouv.fr/" target="_blank" rel="noopener noreferrer" className="text-brand-red hover:underline">Inspection du travail (DREETS)</a> — violences, harcèlement en milieu professionnel.</li>
              <li>• <strong>3919</strong> — Violences femmes info (anonyme, gratuit).</li>
              <li>• <strong>3018</strong> — Cyberharcèlement (jeunes).</li>
            </ul>
          </section>
        </div>
      </div>
    </>
  );
}
